import { useEffect, useState } from 'react'
import { message } from 'antd'
import { Button, TextInput } from '@forwardflow/ui-v2'
import { PhoneIcon, SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import {
   numberSearch,
   prettyPhone,
} from '@forwardflow/features/campaign-v2/utils/phone'
import { campaignV2Api } from '../../services'
import {
   useAvailableNumbers,
   useInventoryNumbers,
   useLiveCampaign,
   useNumberPricing,
} from '../../queries/campaign-v2.queries'
import { useCampaignV2Store } from '../../store/editor.store'
import { useCampaignV2Editor } from '../../hooks/useCampaignV2Editor'
import {
   CheckBoxDot,
   EmptyNote,
   IStepFooter,
   ListCard,
   ListRow,
   Loading,
   Pill,
   StepFrame,
   TextButton,
} from '../shared'

type TPanel = 'none' | 'inventory' | 'new'

/**
 * Step 5 — the numbers leads see. Three sources, all per campaign:
 * numbers the organization already owns (no new charge), a new local
 * number reserved now and bought only at activation, and removal. Every
 * picker call edits the draft, so the revision is reread afterwards.
 */
export const NumbersStep = ({
   footer,
   onSave,
   canSave,
}: {
   footer: IStepFooter
   onSave?: () => void
   canSave?: boolean
}) => {
   const { campaignId, config, dirty } = useCampaignV2Store()
   const locked = !campaignId || dirty
   const { load } = useCampaignV2Editor()
   const { data: live, mutate } = useLiveCampaign(campaignId)
   const {
      data: inventory,
      isLoading: inventoryLoading,
      isValidating: inventoryValidating,
      error: inventoryError,
      mutate: mutateInventory,
   } = useInventoryNumbers()
   const [panel, setPanel] = useState<TPanel>('none')
   const [search, setSearch] = useState('')
   const [debouncedSearch, setDebouncedSearch] = useState('')
   useEffect(() => {
      const timer = setTimeout(
         () => setDebouncedSearch(numberSearch(search)),
         500
      )
      return () => clearTimeout(timer)
   }, [search])
   const [picked, setPicked] = useState<string[]>([])
   const [busy, setBusy] = useState(false)
   const {
      data: available,
      isLoading: availableLoading,
      error: availableError,
      mutate: retrySearch,
   } = useAvailableNumbers(panel === 'new' ? debouncedSearch : null)
   const searching =
      availableLoading || numberSearch(search) !== debouncedSearch

   const selected = config?.phoneNumbers ?? live?.phoneNumbers ?? []
   const unused = (inventoryError ? [] : inventory || []).filter(
      (n) =>
         !n.isUsedInCampaigns &&
         !n.isOrganizationNumber &&
         !selected.includes(n.phoneNumber)
   )
   const { data: pricing } = useNumberPricing(
      panel === 'new' ? Math.max(picked.length, 1) : 0
   )

   const refresh = async () => {
      if (!campaignId) return
      await load(campaignId)
      await mutate()
      await mutateInventory()
   }

   const addFromInventory = async () => {
      if (!campaignId || dirty || !picked.length) return
      setBusy(true)
      try {
         await campaignV2Api().assignNumbers(campaignId, picked)
         await refresh()
         message.success(
            `${picked.length} number${picked.length === 1 ? '' : 's'} added`
         )
         setPicked([])
         setPanel('none')
      } catch (e) {
         message.error((e as Error).message || 'Could not add those numbers')
      } finally {
         setBusy(false)
      }
   }

   const reserve = async () => {
      if (!campaignId || dirty || !picked.length) return
      setBusy(true)
      try {
         for (const phone of picked) {
            const row = (available || []).find((n) => n.phoneNumber === phone)
            if (row) await campaignV2Api().reserveNumber(campaignId, row)
         }
         await refresh()
         message.success(
            `${picked.length} number${picked.length === 1 ? '' : 's'} reserved. Bought when you activate.`
         )
         setPicked([])
         setPanel('none')
      } catch (e) {
         message.error(
            (e as Error).message ||
               'Could not reserve that number. It may have just been taken.'
         )
      } finally {
         setBusy(false)
      }
   }

   const remove = async (phone: string) => {
      if (!campaignId || dirty) return
      setBusy(true)
      try {
         await campaignV2Api().removeNumbers(campaignId, [phone])
         await refresh()
      } catch (e) {
         message.error((e as Error).message || 'Could not remove that number')
      } finally {
         setBusy(false)
      }
   }

   const toggle = (phone: string) =>
      setPicked((p) =>
         p.includes(phone) ? p.filter((x) => x !== phone) : [...p, phone]
      )
   const price = pricing?.currentPeriod?.amount

   return (
      <StepFrame
         eyebrow="Step 5 of 8 · Numbers"
         title="Which numbers should this campaign call and text from?"
         lede="Reuse numbers you already own at no extra cost, or reserve a new local number. New numbers are bought only when you activate."
         footer={{
            ...footer,
            disabled: footer.disabled || !selected.length || busy,
            action: selected.length
               ? footer.action
               : 'Pick at least one number',
         }}
         testId="step-numbers"
      >
         {locked && (
            <div
               className="flex items-center justify-between gap-4 rounded-xl bg-white/[.03] p-4"
               data-testid="number-save-note"
            >
               <p className="text-sm text-white/60">
                  {campaignId
                     ? 'You can browse numbers now. Save your campaign edits before adding or removing a number.'
                     : 'Browse your numbers or search by location or area code. Save a draft with a name, business type and cadence before assigning numbers.'}
               </p>
               {onSave && (
                  <Button
                     size="middle"
                     variant="secondary"
                     onClick={onSave}
                     disabled={!canSave}
                     data-testid="numbers-save-draft"
                  >
                     Save draft
                  </Button>
               )}
            </div>
         )}
         <ListCard
            title={
               selected.length
                  ? `${selected.length} number${selected.length === 1 ? '' : 's'} on this campaign`
                  : 'No number yet'
            }
            testId="selected-numbers"
         >
            {!selected.length && (
               <EmptyNote testId="numbers-empty">
                  Add one of your numbers or reserve a new one below.
               </EmptyNote>
            )}
            {selected.map((n) => {
               const owned = inventory?.find((i) => i.phoneNumber === n)
               const reserved = Boolean(inventory) && !inventoryError && !owned
               return (
                  <ListRow
                     key={n}
                     icon={<PhoneIcon width={15} />}
                     title={prettyPhone(n)}
                     sub={
                        owned?.locality ||
                        (owned
                           ? 'Your number'
                           : reserved
                             ? 'Reserved · bought at activation'
                             : inventoryError
                               ? 'Number details unavailable'
                               : 'Checking number details…')
                     }
                     right={
                        <>
                           {reserved && <Pill tone="warn">new</Pill>}
                           <TextButton
                              onClick={() => void remove(n)}
                              tone="muted"
                              disabled={busy || locked}
                              testId={`remove-number-${n}`}
                           >
                              Remove
                           </TextButton>
                        </>
                     }
                     testId={`number-${n}`}
                  />
               )
            })}
         </ListCard>

         {panel === 'none' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
               <Button
                  variant="secondary"
                  size="middle"
                  className="!h-11"
                  onClick={() => {
                     setPicked([])
                     setPanel('inventory')
                  }}
                  data-testid="open-inventory"
               >
                  Use a number I already own
                  {unused.length ? ` (${unused.length})` : ''}
               </Button>
               <Button
                  variant="secondary"
                  size="middle"
                  className="!h-11"
                  onClick={() => {
                     setPicked([])
                     setPanel('new')
                  }}
                  data-testid="open-new"
               >
                  Reserve a new local number
               </Button>
            </div>
         )}

         {panel !== 'none' && (
            <div
               className="sticky top-0 z-20 flex items-center justify-between gap-4 rounded-xl border border-[#3f3e47] bg-[#292532] px-4 py-3 shadow-sm"
               data-testid="number-picker-actions"
            >
               <div className="min-w-0">
                  <p
                     className="text-sm font-medium text-white"
                     aria-live="polite"
                  >
                     {picked.length} selected
                  </p>
                  <p className="text-xs text-white/50 mt-0.5">
                     {panel === 'new'
                        ? 'Bought only when you activate.'
                        : 'No new purchase cost.'}
                  </p>
               </div>
               <div className="flex items-center gap-4 shrink-0">
                  <TextButton
                     onClick={() => setPanel('none')}
                     tone="muted"
                     disabled={busy}
                     testId="number-picker-close"
                  >
                     Close
                  </TextButton>
                  <Button
                     size="middle"
                     className="!h-9"
                     onClick={() =>
                        panel === 'inventory'
                           ? void addFromInventory()
                           : void reserve()
                     }
                     disabled={
                        !picked.length ||
                        locked ||
                        (panel === 'inventory'
                           ? inventoryLoading ||
                             Boolean(inventoryError) ||
                             !unused.length
                           : searching ||
                             Boolean(availableError) ||
                             !available?.length)
                     }
                     loading={busy}
                     data-testid={
                        panel === 'inventory'
                           ? 'assign-numbers'
                           : 'reserve-numbers'
                     }
                  >
                     {panel === 'inventory' ? 'Add to campaign' : 'Reserve'}
                  </Button>
               </div>
            </div>
         )}

         {panel === 'inventory' && (
            <ListCard
               title="Numbers you own that are free"
               hint="Reuse an unassigned number from your organization at no new purchase cost."
               testId="inventory-panel"
            >
               {inventoryLoading && !inventory && !inventoryError && (
                  <Loading label="Checking your numbers" />
               )}
               {inventoryError && (
                  <EmptyNote>
                     <span role="alert">Could not load your numbers.</span>{' '}
                     <TextButton
                        onClick={() =>
                           void mutateInventory().catch(() => undefined)
                        }
                        disabled={inventoryValidating}
                        testId="inventory-retry"
                     >
                        {inventoryValidating ? 'Retrying…' : 'Try again'}
                     </TextButton>
                  </EmptyNote>
               )}
               {!inventoryLoading &&
                  !inventoryError &&
                  inventory &&
                  !unused.length && (
                     <EmptyNote>
                        Every number you own is already in use. Reserve a new
                        one instead.
                     </EmptyNote>
                  )}
               {unused.map((n) => (
                  <ListRow
                     key={n.phoneNumber}
                     icon={<CheckBoxDot on={picked.includes(n.phoneNumber)} />}
                     title={prettyPhone(n.phoneNumber)}
                     sub={
                        [n.locality, n.region].filter(Boolean).join(', ') ||
                        undefined
                     }
                     onClick={() => toggle(n.phoneNumber)}
                     selected={picked.includes(n.phoneNumber)}
                     testId={`inventory-${n.phoneNumber}`}
                  />
               ))}
            </ListCard>
         )}

         {panel === 'new' && (
            <ListCard
               title="Reserve a new local number"
               hint={
                  typeof price === 'number'
                     ? `Estimated current-period cost: $${price.toFixed(2)} for ${Math.max(picked.length, 1)} number(s). Charged when you activate.`
                     : 'Charged only when you activate the campaign.'
               }
               testId="new-panel"
            >
               <div className="px-4 py-3">
                  <TextInput
                     size="sm"
                     leftSection={
                        <SearchMagnifyingGlassIcon className="w-4 h-4" />
                     }
                     shouldLeftSectionBeFocused
                     placeholder="Search by area code, city, state or number"
                     aria-label="Search available phone numbers"
                     value={search}
                     onChange={(e) => {
                        setSearch(e.currentTarget.value)
                        setPicked([])
                     }}
                     data-testid="area-search"
                  />
               </div>
               {searching && <Loading label="Looking for numbers" />}
               {!searching && availableError && (
                  <EmptyNote>
                     <span role="alert">
                        Could not search for numbers.{' '}
                        {(availableError as Error).message}
                     </span>{' '}
                     <TextButton onClick={() => void retrySearch()}>
                        Try again
                     </TextButton>
                  </EmptyNote>
               )}
               {!searching && !availableError && !available?.length && (
                  <EmptyNote>
                     No numbers found
                     {debouncedSearch ? ` for “${debouncedSearch}”` : ''}. Try
                     another city, state or area code.
                  </EmptyNote>
               )}
               {(!searching && !availableError ? available || [] : []).map(
                  (n) => (
                     <ListRow
                        key={n.phoneNumber}
                        icon={
                           <CheckBoxDot on={picked.includes(n.phoneNumber)} />
                        }
                        title={prettyPhone(n.phoneNumber)}
                        sub={
                           [n.locality, n.region].filter(Boolean).join(', ') ||
                           undefined
                        }
                        onClick={() => toggle(n.phoneNumber)}
                        selected={picked.includes(n.phoneNumber)}
                        testId={`available-${n.phoneNumber}`}
                     />
                  )
               )}
            </ListCard>
         )}
      </StepFrame>
   )
}
